'use client';

import { useMemo } from 'react';
import type { CalendarEvent, Significance } from '@/lib/types';
import type { LongWeekendDayType } from '@/lib/long-weekends';
import { formatMonthName, formatDayHeader } from '@/lib/date-format';
import { uiFb } from '@/lib/i18n';

const SIG_RANK: Record<Significance, number> = { critical: 0, highest: 1, high: 2, major: 3, notable: 4, medium: 5, low: 6 };

function getTopEvents(dayMap: Record<number, CalendarEvent[]>, limit: number): CalendarEvent[] {
  const seen = new Set<string>();
  const all: CalendarEvent[] = [];
  for (const evts of Object.values(dayMap)) {
    for (const e of evts) {
      if (!seen.has(e.id)) {
        seen.add(e.id);
        all.push(e);
      }
    }
  }
  all.sort((a, b) => SIG_RANK[a.significance] - SIG_RANK[b.significance] || a.dateStart.localeCompare(b.dateStart));
  return all.slice(0, limit);
}

function groupByDayString(events: CalendarEvent[], year: number, month: number): Record<number, CalendarEvent[]> {
  const map: Record<number, CalendarEvent[]> = {};
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const mm = String(month + 1).padStart(2, '0');
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${mm}-${String(d).padStart(2, '0')}`;
    for (const e of events) {
      if (e.dateStart <= dateStr && e.dateEnd >= dateStr) {
        if (!map[d]) map[d] = [];
        map[d].push(e);
      }
    }
  }
  return map;
}

interface MiniMonthProps {
  year: number;
  month: number;
  events: CalendarEvent[];
  compact?: boolean;
  lang?: string;
  highlightDates?: Map<string, LongWeekendDayType> | null;
  eventSlugMap?: Record<string, string>;
}

export function MiniMonth({ year, month, events, compact, lang = 'en', highlightDates, eventSlugMap }: MiniMonthProps) {
  const dayMap = useMemo(() => groupByDayString(events, year, month), [events, year, month]);
  const firstDayOfWeek = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;
  const todayDay = isCurrentMonth ? today.getDate() : -1;

  const dayHeaders = useMemo(() => Array.from({ length: 7 }, (_, i) => formatDayHeader(i, lang)), [lang]);

  const eventCount = Object.values(dayMap).reduce((sum, arr) => sum + arr.length, 0);

  const eventLimit = compact ? 3 : 4;
  const topEvents = useMemo(() => getTopEvents(dayMap, eventLimit), [dayMap, eventLimit]);
  const totalUniqueEvents = useMemo(() => {
    const seen = new Set<string>();
    for (const evts of Object.values(dayMap)) {
      for (const e of evts) seen.add(e.id);
    }
    return seen.size;
  }, [dayMap]);
  const moreCount = totalUniqueEvents - topEvents.length;

  const dateFmt = useMemo(() => new Intl.DateTimeFormat(lang, { month: 'short', day: 'numeric' }), [lang]);

  const totalCells = 42; // Always 6 rows × 7 cols for consistent height
  const cells: React.ReactNode[] = [];

  for (let i = 0; i < firstDayOfWeek; i++) {
    cells.push(<div key={`blank-${i}`} />);
  }

  const mm = String(month + 1).padStart(2, '0');

  const nowDate = new Date();
  const todayDate = new Date(nowDate.getFullYear(), nowDate.getMonth(), nowDate.getDate());

  for (let d = 1; d <= daysInMonth; d++) {
    const dayEvents = dayMap[d] || [];
    const hasEvents = dayEvents.length > 0;
    const isToday = d === todayDay;
    const isPastDay = new Date(year, month, d) < todayDate;
    const heatLevel = dayEvents.length >= 3 ? 'day-heat-3' : dayEvents.length === 2 ? 'day-heat-2' : '';

    // Long weekend highlight type
    const dateKey = `${year}-${mm}-${String(d).padStart(2, '0')}`;
    const hlType = highlightDates?.get(dateKey);
    const hlBg = hlType === 'bridge' ? 'bg-semantic-warning/10'
      : hlType === 'holiday' ? 'bg-semantic-success/10'
      : hlType === 'weekend' ? 'bg-semantic-success/5'
      : '';

    // Link to the most significant event's detail page if slug available
    const topEventSlug = hasEvents ? eventSlugMap?.[dayEvents[0].id] : undefined;
    const cellClasses = `relative flex h-6 w-6 items-center justify-center rounded-sm text-caption leading-none tabular-nums md:h-7 md:w-7 md:text-label ${isPastDay ? 'opacity-40 grayscale' : ''} ${heatLevel} ${hlBg} ${
      isToday
        ? 'rounded-full bg-brand/20 font-bold text-brand'
        : hasEvents
          ? 'font-medium text-text-primary'
          : hlType
            ? 'text-text-secondary'
            : 'text-text-dim'
    }`;
    const dotIndicators = hasEvents && (
      <span className="absolute bottom-0 left-1/2 flex -translate-x-1/2 gap-[1px]">
        {dayEvents.slice(0, 3).map((e, i) => (
          <span
            key={i}
            className="block h-[5px] w-[5px] rounded-full md:h-[6px] md:w-[6px]"
            style={{ backgroundColor: e.color }}
          />
        ))}
      </span>
    );

    cells.push(
      topEventSlug ? (
        <a
          key={d}
          href={`/events/${topEventSlug}`}
          title={dayEvents[0].title}
          className={`${cellClasses} cursor-pointer hover:bg-surface-hover/50`}
        >
          {d}
          {dotIndicators}
        </a>
      ) : (
        <div key={d} className={cellClasses}>
          {d}
          {dotIndicators}
        </div>
      )
    );
  }

  // Pad to 42 cells (6 rows) for consistent card height
  const currentCells = firstDayOfWeek + daysInMonth;
  for (let i = currentCells; i < totalCells; i++) {
    cells.push(<div key={`trail-${i}`} />);
  }

  const monthName = formatMonthName(month, lang);

  if (compact) {
    return (
      <div className="overflow-hidden rounded-xl border border-border-subtle bg-surface-card shadow-lg">
        <div className="h-[3px] bg-gradient-to-r from-brand/40 via-brand to-brand/40" />
        <div className="p-2 md:p-3">
          <h3 className="mb-1 font-heading text-caption font-bold uppercase tracking-wider text-text-secondary md:text-xs">
            {monthName}
          </h3>
          <div className="grid grid-cols-7 gap-0">
            {cells}
          </div>
          {topEvents.length > 0 && (
            <div className="mt-1.5 border-t border-border-subtle pt-1.5">
              {topEvents.map((e) => {
                const slug = eventSlugMap?.[e.id];
                return (
                  <div key={e.id} className="flex items-center gap-1.5 py-[1px]">
                    <span className="h-[5px] w-[5px] shrink-0 rounded-full" style={{ backgroundColor: e.color }} />
                    <span className="text-caption tabular-nums text-text-dim">{dateFmt.format(new Date(e.dateStart + 'T00:00'))}</span>
                    {slug
                      ? <a href={`/events/${slug}`} className="truncate text-caption text-text-secondary hover:text-brand hover:underline">{e.title}</a>
                      : <span className="truncate text-caption text-text-secondary">{e.title}</span>}
                  </div>
                );
              })}
              {moreCount > 0 && (
                <div className="pt-[1px] text-caption text-text-dim">{uiFb('ui.more_events', '+{count} more', lang, { count: String(moreCount) })}</div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-border-subtle bg-surface-card">
      <div className="h-[3px] bg-gradient-to-r from-brand/40 via-brand to-brand/40" />
      <div className="p-3 md:p-4">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-heading text-xs font-bold uppercase tracking-wider text-text-secondary md:text-sm">
            {monthName}
          </h3>
          {eventCount > 0 && (
            <span className="rounded-full bg-brand/15 px-1.5 py-0.5 text-caption font-semibold text-brand md:text-label">
              {eventCount}
            </span>
          )}
        </div>
        <div className="mb-1 grid grid-cols-7 gap-0">
          {dayHeaders.map((d, i) => (
            <div key={i} className="flex h-5 w-6 items-center justify-center text-caption font-medium uppercase text-muted md:w-7 md:text-caption">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-0">
          {cells}
        </div>
        {topEvents.length > 0 && (
          <div className="mt-2 border-t border-border-subtle pt-2">
            {topEvents.map((e) => {
              const slug = eventSlugMap?.[e.id];
              return (
                <div key={e.id} className="flex items-center gap-1.5 py-[2px]">
                  <span className="h-[5px] w-[5px] shrink-0 rounded-full" style={{ backgroundColor: e.color }} />
                  <span className="text-caption tabular-nums text-text-dim">{dateFmt.format(new Date(e.dateStart + 'T00:00'))}</span>
                  {slug
                    ? <a href={`/events/${slug}`} className="truncate text-caption text-text-secondary hover:text-brand hover:underline">{e.title}</a>
                    : <span className="truncate text-caption text-text-secondary">{e.title}</span>}
                </div>
              );
            })}
            {moreCount > 0 && (
              <div className="pt-0.5 text-caption text-text-dim">{uiFb('ui.more_events', '+{count} more', lang, { count: String(moreCount) })}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
